import { ReactElement } from 'react';
import { getThemeProps } from '@/components/layoutUtil';
import { HEADER_PHRASES } from '@/consts/phrases/headerPhrases';
import { IBadgeDetails } from '@/store/accountStore';
import { Theme } from '@mui/material';
export interface IMenuItem {
    phraseId: number;
    href: string;
    invisible?: boolean;
    openInNewTab?: boolean;
    icon?: ReactElement;
    className?: string;
}

export interface IAuthMenuItem {
    phraseId: number;
    basePath?: string;
    href: string;
    data: boolean | number | undefined | null;
    icon?: ReactElement;
    className?: string;
}

export const MOBILE_HEADER_HEIGHT = '67px';

export const getMobileHeaderStyles = (
    open: boolean,
    headerHeight: string = MOBILE_HEADER_HEIGHT,
) => {
    const listSX = (theme: Theme) => ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px 0',
        borderRadius: theme.borderRadius.topMenu,
    });

    const listItemSX = (theme: Theme) => ({
        display: 'flex',
        width: 'auto',
        cursor: 'pointer',
        color: theme.colors.other.text.header,
        justifyContent: 'center',
        alignItems: 'center',
        padding: '14px',
        margin: '0',
        position: 'relative',
        '&:hover': {
            color: theme.colors.other.text.header,
        },
    });

    const activeListItemSX = (theme: Theme) => ({
        ...listItemSX(theme),
        color: theme.colors.other.text.header,
        backgroundColor: theme.colors.other.background.highlightHover,
        borderRadius: theme.borderRadius.primary,
        width: 'calc(100vw - 48px)',
    });

    const boxItemSX = (theme: Theme) => ({
        position: 'absolute',
        top: open ? '100%' : '90%',
        left: 0,
        zIndex: 20,
        width: '100%',
        maxHeight: `calc(100vh - ${headerHeight})`,
        transition: 'all 0.2s ease-in-out',
        backgroundColor: theme.colors.other.background.topMenu,
        boxShadow: '0px 6px 6px -1px rgba(0 ,0, 0, 0.1)',
        opacity: open ? '100%' : '0%',
        overflowY: 'auto',
        borderRadius: theme.borderRadius.mobileTopMenu,
    });

    return { listSX, listItemSX, activeListItemSX, boxItemSX };
};

export const getMenuItems = (
    blogUrl?: string,
    faqUrl?: string,
    contactUrl?: string,
    helpCentreUrl?: string,
): IMenuItem[] => {
    const { helpCentreInsteadFaq } = getThemeProps();

    return [
        {
            phraseId: HEADER_PHRASES.ABOUT,
            href: '/about',
        },
        {
            phraseId: helpCentreInsteadFaq ? HEADER_PHRASES.HELP_CENTRE : HEADER_PHRASES.FAQ,
            href: (helpCentreInsteadFaq ? helpCentreUrl : faqUrl) || '/faq',
            openInNewTab: helpCentreInsteadFaq ? !!helpCentreUrl : !!faqUrl,
        },
        {
            phraseId: HEADER_PHRASES.BLOG,
            href: blogUrl ?? '',
            invisible: !blogUrl,
            openInNewTab: true,
        },
        {
            phraseId: HEADER_PHRASES.CONTACT_US,
            href: contactUrl || '/contact',
            openInNewTab: !!contactUrl,
        },
    ];
};

export const getAuthMenuItems = (badgeDetails: IBadgeDetails): IAuthMenuItem[] => {
    const hrefOrder = [
        '/surveys',
        '/referrals',
        '/profile',
        '/account/personal-details',
        '/wallet',
    ];
    const sortItems = (a: IAuthMenuItem, b: IAuthMenuItem) =>
        hrefOrder.indexOf(a.href) - hrefOrder.indexOf(b.href);

    const defaultItems = [
        {
            phraseId: HEADER_PHRASES.SURVEYS,
            href: '/surveys',
            data: badgeDetails.surveyQty,
        },
        {
            phraseId: HEADER_PHRASES.PROFILE,
            href: '/profile',
            data: badgeDetails.hasProfileIcon,
        },
        {
            phraseId: HEADER_PHRASES.ACCOUNT,
            basePath: '/account',
            href: '/account/personal-details',
            data: badgeDetails.hasAccountIcon,
        },
        {
            phraseId: HEADER_PHRASES.WALLET,
            href: '/wallet',
            data: badgeDetails.hasWalletIcon,
        },
    ];

    const defaultItemsWithReferrals = [
        ...defaultItems,
        {
            phraseId: HEADER_PHRASES.REFERRALS,
            href: '/referrals',
            data: badgeDetails.referralQty,
        },
    ];

    const getAuthItems = () => {
        if (badgeDetails.referralQty !== null) {
            return defaultItemsWithReferrals;
        } else {
            return defaultItems;
        }
    };

    return getAuthItems().sort(sortItems);
};
